import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import {
  fetchPartners,
  createPartner,
  updatePartner,
  deletePartner,
  clearShowcasePosition,
  clearProjectsHero,
} from '@/services/partnerService';
import { fetchProducts } from '@/services/productService';
import { Partner, Project } from '@/types/partner';
import { Product } from '@/types/product';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Edit, Trash2, Plus, ExternalLink, MapPin, FolderOpen, ChevronDown, Search, Star, Save, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/context/LanguageContext';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import {
  convertImagesToAvif,
  type AvifConversionProgress,
} from '@/services/imageConversionService';
import enPartners from '@/locales/en/partners.json';
import frPartners from '@/locales/fr/partners.json';
import { cn } from '@/lib/utils';

type PartnersDict = typeof enPartners;

const PartnersManagement: React.FC = () => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);
  const [partnerSearch, setPartnerSearch] = useState('');
  const [expandedPartners, setExpandedPartners] = useState<Record<string, boolean>>({});
  const [formData, setFormData] = useState<Omit<Partner, 'id' | 'created_at' | 'updated_at'>>({
    name: '',
    logo: '',
    website: '',
    description: '',
    location: '',
    projects: [],
  });
  const [projectFormData, setProjectFormData] = useState<Omit<Project, 'id'>>({
    title: '',
    description: '',
    images: [],
    products: [],
    location: '',
    project_category: 'All',
    showcase: false,
    showcase_position: null,
    is_projects_hero: false,
  });
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [projectImages, setProjectImages] = useState<File[]>([]);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [isConvertingImages, setIsConvertingImages] = useState(false);
  const [conversionProgress, setConversionProgress] = useState<AvifConversionProgress | null>(null);
  
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { language, tl } = useLanguage();

  const partnersDict: PartnersDict = language === 'en' ? enPartners : frPartners;
  const t = (key: string, vars?: Record<string, string>) => {
    const path = key.startsWith('partners.') ? key.slice('partners.'.length) : key;
    const value = path.split('.').reduce<unknown>((acc, part) => {
      if (acc && typeof acc === 'object' && part in (acc as object)) {
        return (acc as Record<string, unknown>)[part];
      }
      return undefined;
    }, partnersDict);
    if (typeof value !== 'string') return key;
    if (!vars) return value;
    return Object.entries(vars).reduce(
      (text, [k, v]) => text.replace(`{{${k}}}`, v).replace(`{${k}}`, v),
      value
    );
  };

  // Fetch partners
  const { data: partners = [], isLoading } = useQuery({
    queryKey: ['partners'],
    queryFn: fetchPartners,
  });

  const filteredPartners = useMemo(() => {
    const q = partnerSearch.trim().toLowerCase();
    if (!q) return partners;
    return partners.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.location || '').toLowerCase().includes(q) ||
        (p.description || '').toLowerCase().includes(q) ||
        p.projects?.some((project) => project.title.toLowerCase().includes(q))
    );
  }, [partners, partnerSearch]);

  const togglePartnerExpanded = (partnerId: string) => {
    setExpandedPartners((prev) => ({
      ...prev,
      [partnerId]: !(prev[partnerId] ?? true),
    }));
  };

  const isPartnerExpanded = (partnerId: string) => expandedPartners[partnerId] ?? true;

  // Fetch products
  const { data: allProducts = [] } = useQuery<Product[]>({
    queryKey: ['products'],
    queryFn: async () => {
      const products = await fetchProducts({});
      return products;
    },
  });

  const filteredProducts = useMemo(() => {
    const q = productSearchQuery.trim().toLowerCase();
    if (!q) return allProducts;
    return allProducts.filter(
      (product) =>
        product.name.toLowerCase().includes(q) ||
        (product.description || '').toLowerCase().includes(q) ||
        (product.category || '').toLowerCase().includes(q)
    );
  }, [allProducts, productSearchQuery]);

  const toggleProjectProduct = (productName: string) => {
    setProjectFormData((prev) => {
      const current = prev.products || [];
      const exists = current.includes(productName);
      return {
        ...prev,
        products: exists
          ? current.filter((name) => name !== productName)
          : [...current, productName],
      };
    });
  };

  // Upload logo to Supabase Storage
  const uploadLogo = async (file: File): Promise<string> => {
    setIsUploading(true);
    
    try {
      const fileName = `partner-logo-${Date.now()}.${file.name.split('.').pop()}`;
      
      const { error: uploadError, data: uploadData } = await supabase.storage
        .from('partners')
        .upload(`logos/${fileName}`, file, {
          upsert: true,
          cacheControl: '3600'
        });
        
      if (uploadError) {
        console.error('Error uploading logo:', uploadError);
        throw uploadError;
      }
      
      const { data: urlData } = supabase.storage
        .from('partners')
        .getPublicUrl(`logos/${fileName}`);
        
      return urlData.publicUrl;
    } catch (error) {
      console.error('Error in logo upload process:', error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  // Upload project images (already converted to AVIF when selected)
  const uploadProjectImages = async (files: File[]): Promise<string[]> => {
    setIsUploading(true);
    const uploadedUrls: string[] = [];
    
    try {
      for (const file of files) {
        const ext = file.name.toLowerCase().endsWith('.avif')
          ? 'avif'
          : (file.name.split('.').pop() || 'avif');
        const fileName = `project-${Date.now()}-${Math.random().toString(36).substring(7)}.${ext}`;
        
        const { error: uploadError } = await supabase.storage
          .from('partnerprojects')
          .upload(`images/${fileName}`, file, {
            upsert: true,
            cacheControl: '3600',
            contentType: file.type || 'image/avif',
          });
          
        if (uploadError) {
          console.error('Error uploading image:', uploadError);
          throw uploadError;
        }
        
        const { data: urlData } = supabase.storage
          .from('partnerprojects')
          .getPublicUrl(`images/${fileName}`);
          
        uploadedUrls.push(urlData.publicUrl);
      }
      
      return uploadedUrls;
    } catch (error) {
      console.error('Error in project images upload process:', error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  // Create partner mutation
  const createPartnerMutation = useMutation({
    mutationFn: async (partnerData: Omit<Partner, 'id' | 'created_at' | 'updated_at'>) => {
      console.log('Mutation function called with data:', partnerData);
      let data = { ...partnerData };
      
      if (logoFile) {
        try {
          console.log('Uploading logo file...');
          const publicUrl = await uploadLogo(logoFile);
          data.logo = publicUrl;
          console.log('Logo uploaded successfully:', publicUrl);
        } catch (error) {
          console.error('Error uploading logo:', error);
          toast({ 
            title: 'Error', 
            description: 'Failed to upload logo image', 
            variant: 'destructive' 
          });
          throw error;
        }
      }
      
      console.log('Creating partner with data:', data);
      return createPartner(data);
    },
    onSuccess: () => {
      console.log('Partner created successfully');
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast({ title: 'Success', description: 'Partner has been created successfully' });
      setIsAddDialogOpen(false);
      resetForm();
    },
    onError: (error) => {
      console.error('Error in createPartnerMutation:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to create partner', 
        variant: 'destructive' 
      });
    }
  });

  // Update partner mutation
  const updatePartnerMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Partner> }) => {
      console.log('Update partner mutation called with:', { id, data });
      console.log('Logo file:', logoFile);
      
      // Remove projects from the data as it's not part of the partners table schema
      const { projects, ...partnerData } = data;
      
      return updatePartner(id, partnerData, logoFile);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast({ title: 'Success', description: 'Partner has been updated successfully' });
      setIsEditDialogOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast({ 
        title: 'Error', 
        description: 'Failed to update partner', 
        variant: 'destructive' 
      });
      console.error('Update partner error:', error);
    }
  });

  // Add project mutation
  const addProjectMutation = useMutation({
    mutationFn: async ({ partnerId, project }: { partnerId: string; project: Omit<Project, 'id'> }) => {
      console.log('Project mutation function called');
      console.log('Partner ID:', partnerId);
      console.log('Project data:', project);
      
      let projectData = { ...project };
      
      if (projectImages.length > 0) {
        try {
          console.log('Uploading project images...');
          const imageUrls = await uploadProjectImages(projectImages);
          projectData.images = imageUrls;
          console.log('Images uploaded successfully:', imageUrls);
        } catch (error) {
          console.error('Error uploading project images:', error);
          toast({ 
            title: 'Error', 
            description: 'Failed to upload project images', 
            variant: 'destructive' 
          });
          throw error;
        }
      }

      console.log('Creating project with data:', projectData);
      const rawPos = projectData.showcase_position != null ? Number(projectData.showcase_position) : null;
      const position = rawPos === 1 || rawPos === 2 || rawPos === 3 ? rawPos : null;
      if (position) {
        await clearShowcasePosition(position);
      }
      if (projectData.is_projects_hero) {
        await clearProjectsHero();
      }
      // Insert the project into the projects table
      const { data, error } = await supabase
        .from('projects')
        .insert([{
          partner_id: partnerId,
          title: projectData.title,
          description: projectData.description,
          images: projectData.images,
          products: projectData.products,
          location: projectData.location,
          project_category: projectData.project_category,
          showcase: position != null,
          showcase_position: position,
          is_projects_hero: !!projectData.is_projects_hero,
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating project:', error);
        throw error;
      }

      console.log('Project created successfully:', data);
      return data;
    },
    onSuccess: () => {
      console.log('Project added successfully');
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      queryClient.invalidateQueries({ queryKey: ['showcase-projects'] });
      toast({ title: 'Success', description: 'Project has been added successfully' });
      setIsProjectDialogOpen(false);
      resetProjectForm();
    },
    onError: (error) => {
      console.error('Error in addProjectMutation:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to add project', 
        variant: 'destructive' 
      });
    }
  });

  // Delete partner mutation
  const deletePartnerMutation = useMutation({
    mutationFn: deletePartner,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast({ title: 'Success', description: 'Partner has been deleted successfully' });
    },
    onError: (error) => {
      toast({ 
        title: 'Error', 
        description: 'Failed to delete partner', 
        variant: 'destructive' 
      });
      console.error('Delete partner error:', error);
    }
  });

  // Update project mutation
  const updateProjectMutation = useMutation({
    mutationFn: async ({ projectId, project }: { projectId: string; project: Partial<Project> }) => {
      let projectData = { ...project };
      
      if (projectImages.length > 0) {
        try {
          const imageUrls = await uploadProjectImages(projectImages);
          projectData.images = [...(projectData.images || []), ...imageUrls];
        } catch (error) {
          toast({ 
            title: 'Error', 
            description: 'Failed to upload project images', 
            variant: 'destructive' 
          });
          throw error;
        }
      }

      const rawPos = projectData.showcase_position != null ? Number(projectData.showcase_position) : null;
      const position = rawPos === 1 || rawPos === 2 || rawPos === 3 ? rawPos : null;

      if (position) {
        await clearShowcasePosition(position, projectId);
      }
      if (projectData.is_projects_hero) {
        await clearProjectsHero(projectId);
      }

      const payload = {
        title: projectData.title,
        description: projectData.description,
        images: projectData.images,
        products: projectData.products,
        location: projectData.location,
        project_category: projectData.project_category,
        showcase: position != null,
        showcase_position: position,
        is_projects_hero: !!projectData.is_projects_hero,
      };
      
      const { data, error } = await supabase
        .from('projects')
        .update(payload)
        .eq('id', projectId)
        .select()
        .single();

      if (error) {
        console.error('Update project error payload:', payload, error);
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      queryClient.invalidateQueries({ queryKey: ['showcase-projects'] });
      toast({ title: 'Success', description: 'Project has been updated successfully' });
      setIsProjectDialogOpen(false);
      resetProjectForm();
      setSelectedProject(null);
    },
    onError: (error: unknown) => {
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message: string }).message)
          : 'Failed to update project';
      toast({
        title: 'Error',
        description: message.includes('showcase_position')
          ? tl(
              'Colonne showcase_position manquante — exécutez la migration SQL.',
              'Missing showcase_position column — run the SQL migration.',
              'Colonna showcase_position mancante — esegui la migration SQL.'
            )
          : message,
        variant: 'destructive',
      });
      console.error('Update project error:', error);
    }
  });

  // Delete project mutation
  const deleteProjectMutation = useMutation({
    mutationFn: async (projectId: string) => {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      queryClient.invalidateQueries({ queryKey: ['showcase-projects'] });
      toast({ title: 'Success', description: 'Project has been deleted successfully' });
    },
    onError: (error) => {
      toast({ 
        title: 'Error', 
        description: 'Failed to delete project', 
        variant: 'destructive' 
      });
      console.error('Delete project error:', error);
    }
  });

  const resetForm = () => {
    console.log('resetForm called');
    setFormData({
      name: '',
      logo: '',
      website: '',
      description: '',
      location: '',
      projects: [],
    });
    setLogoFile(null);
    setLogoPreview(null);
    setSelectedPartnerId(null);
  };

  const resetProjectForm = () => {
    setProjectFormData({
      title: '',
      description: '',
      images: [],
      products: [],
      location: '',
      project_category: 'All',
      showcase: false,
      showcase_position: null,
      is_projects_hero: false,
    });
    setProjectImages([]);
    setProductSearchQuery('');
    setIsConvertingImages(false);
    setConversionProgress(null);
  };

  const handleAddPartner = () => {
    console.log('handleAddPartner called');
    console.log('Form data:', formData);
    console.log('Logo file:', logoFile);
    
    if (!formData.name) {
      console.log('Name is required');
      toast({
        title: 'Error',
        description: 'Partner name is required',
        variant: 'destructive'
      });
      return;
    }

    if (!logoFile && !formData.logo) {
      console.log('Logo is required');
      toast({
        title: 'Error',
        description: 'Partner logo is required',
        variant: 'destructive'
      });
      return;
    }

    try {
      console.log('Starting partner creation...');
      createPartnerMutation.mutate(formData);
    } catch (error) {
      console.error('Error in handleAddPartner:', error);
      toast({
        title: 'Error',
        description: 'Failed to create partner',
        variant: 'destructive'
      });
    }
  };

  const handleUpdatePartner = () => {
    if (selectedPartnerId) {
      console.log('handleUpdatePartner called with:', { selectedPartnerId, formData });
      
      // Ensure we have the required fields
      if (!formData.name) {
        toast({
          title: 'Error',
          description: 'Partner name is required',
          variant: 'destructive'
        });
        return;
      }
      
      updatePartnerMutation.mutate({
        id: selectedPartnerId,
        data: formData
      });
    }
  };

  const handleAddProjectClick = (partnerId: string) => {
    console.log('Setting selected partner ID:', partnerId);
    setSelectedPartnerId(partnerId);
    setSelectedProject(null);
    setProjectFormData({
      title: '',
      description: '',
      images: [],
      products: [],
      location: '',
      project_category: 'All',
      showcase: false,
      showcase_position: null,
      is_projects_hero: false,
    });
    setProjectImages([]);
    setProductSearchQuery('');
    setIsProjectDialogOpen(true);
  };

  const handleAddProject = () => {
    console.log('handleAddProject called');
    console.log('Project form data:', projectFormData);
    console.log('Selected partner ID:', selectedPartnerId);
    console.log('Project images:', projectImages);

    if (!selectedPartnerId) {
      console.log('No partner selected');
      toast({
        title: 'Error',
        description: 'No partner selected',
        variant: 'destructive'
      });
      return;
    }

    if (!projectFormData.title || !projectFormData.description) {
      console.log('Title and description are required');
      toast({
        title: 'Error',
        description: 'Project title and description are required',
        variant: 'destructive'
      });
      return;
    }

    try {
      console.log('Starting project creation...');
      addProjectMutation.mutate({
        partnerId: selectedPartnerId,
        project: projectFormData
      });
    } catch (error) {
      console.error('Error in handleAddProject:', error);
      toast({
        title: 'Error',
        description: 'Failed to add project',
        variant: 'destructive'
      });
    }
  };

  const handleEditClick = (partner: Partner) => {
    console.log('handleEditClick called with partner:', partner);
    
    setFormData({
      name: partner.name,
      logo: partner.logo,
      website: partner.website || '',
      description: partner.description || '',
      location: partner.location || '',
      projects: partner.projects || [],
    });
    setLogoPreview(partner.logo);
    setSelectedPartnerId(partner.id);
    setIsEditDialogOpen(true);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setLogoPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProjectImagesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files ? Array.from(e.target.files) : [];
    // Allow selecting the same files again later
    e.target.value = '';
    if (!selected.length) return;

    setIsConvertingImages(true);
    setConversionProgress({
      overall: 0,
      currentIndex: 1,
      total: selected.length,
      fileName: selected[0].name,
      stage: 'converting',
      message: t('partners.convertingImages') || 'Conversion AVIF en cours…',
    });

    try {
      const avifFiles = await convertImagesToAvif(selected, setConversionProgress);
      setProjectImages((prev) => [...prev, ...avifFiles]);
      toast({
        title: t('partners.conversionDone') || 'Images converties',
        description:
          t('partners.conversionDoneDescription', { count: avifFiles.length }) ||
          `${avifFiles.length} image(s) convertie(s) en AVIF`,
      });
    } catch (error) {
      console.error('AVIF conversion failed:', error);
      toast({
        title: t('partners.conversionFailed') || 'Conversion échouée',
        description:
          error instanceof Error
            ? error.message
            : (t('partners.conversionFailedDescription') ||
              'Impossible de convertir les images en AVIF.'),
        variant: 'destructive',
      });
    } finally {
      setIsConvertingImages(false);
      setTimeout(() => setConversionProgress(null), 800);
    }
  };

  const handleEditProjectClick = (project: Project) => {
    const rawPos = project.showcase_position != null ? Number(project.showcase_position) : null;
    const position =
      rawPos === 1 || rawPos === 2 || rawPos === 3 ? (rawPos as 1 | 2 | 3) : null;

    setSelectedProject(project);
    setProjectFormData({
      title: project.title,
      description: project.description,
      images: project.images || [],
      products: project.products || [],
      location: project.location || '',
      project_category: project.project_category || 'All',
      showcase: position != null || !!project.showcase,
      showcase_position: position,
      is_projects_hero: project.is_projects_hero ?? false,
    });
    setProjectImages([]);
    setProductSearchQuery('');
    setIsProjectDialogOpen(true);
  };

  const handleShowcasePositionChange = (value: 'none' | '1' | '2' | '3') => {
    if (value === 'none') {
      setProjectFormData((prev) => ({
        ...prev,
        showcase: false,
        showcase_position: null,
      }));
      return;
    }
    const position = Number(value) as 1 | 2 | 3;
    setProjectFormData((prev) => ({
      ...prev,
      showcase: true,
      showcase_position: position,
    }));
  };

  const handleUpdateProject = () => {
    if (!selectedProject) return;

    if (!projectFormData.title || !projectFormData.description) {
      toast({
        title: 'Error',
        description: 'Project title and description are required',
        variant: 'destructive'
      });
      return;
    }

    try {
      updateProjectMutation.mutate({
        projectId: selectedProject.id,
        project: projectFormData
      });
    } catch (error) {
      console.error('Error in handleUpdateProject:', error);
      toast({
        title: 'Error',
        description: 'Failed to update project',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="space-y-6 text-black">
      <Helmet>
        <title>{t('partners.title')} | Admin</title>
      </Helmet>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-black">
            {tl('Partenaires & projets', 'Partners & projects', 'Partner e progetti')}
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            {tl(
              'Gérez vos partenaires et prévisualisez / modifiez leurs projets.',
              'Manage partners and preview / edit their projects.',
              'Gestisci i partner e anteprima / modifica i loro progetti.'
            )}
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)} className="rounded-none">
          <Plus size={16} className="mr-2" /> {t('partners.addPartner')}
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <Input
          value={partnerSearch}
          onChange={(e) => setPartnerSearch(e.target.value)}
          placeholder={tl(
            'Rechercher un partenaire ou un projet…',
            'Search a partner or project…',
            'Cerca un partner o un progetto…'
          )}
          className="rounded-none border-neutral-300 bg-white pl-9"
        />
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 text-neutral-500">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-black" />
          <p className="mt-4 text-sm">{t('partners.loading')}</p>
        </div>
      ) : filteredPartners.length === 0 ? (
        <Card className="rounded-none border-neutral-200 shadow-none">
          <CardContent className="flex flex-col items-center py-16 text-center">
            <FolderOpen className="mb-3 h-10 w-10 text-neutral-300" />
            <p className="text-sm text-neutral-500">
              {partners.length === 0 ? t('partners.noPartners') : tl('Aucun résultat', 'No results', 'Nessun risultato')}
            </p>
            {partners.length === 0 && (
              <Button className="mt-6 rounded-none" onClick={() => setIsAddDialogOpen(true)}>
                <Plus size={16} className="mr-2" /> {t('partners.addPartner')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredPartners.map((partner) => {
            const projects = partner.projects || [];
            const expanded = isPartnerExpanded(partner.id);

            return (
              <Card
                key={partner.id}
                className="overflow-hidden rounded-none border-neutral-200 bg-white shadow-none"
              >
                <div className="flex flex-col gap-4 border-b border-neutral-100 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                  <div className="flex min-w-0 items-start gap-4">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden border border-neutral-200 bg-neutral-50">
                      <img
                        src={partner.logo}
                        alt={partner.name}
                        className="max-h-full max-w-full object-contain p-1.5"
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="truncate text-base font-semibold uppercase tracking-[0.06em]">
                          {partner.name}
                        </h2>
                        <Badge variant="secondary" className="rounded-none font-normal">
                          {projects.length}{' '}
                          {tl(
                            projects.length === 1 ? 'projet' : 'projets',
                            projects.length === 1 ? 'project' : 'projects',
                            projects.length === 1 ? 'progetto' : 'progetti'
                          )}
                        </Badge>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
                        {partner.location ? (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5" />
                            {partner.location}
                          </span>
                        ) : null}
                        {partner.website ? (
                          <a
                            href={partner.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-neutral-700 underline-offset-2 hover:underline"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            {tl('Site web', 'Website', 'Sito web')}
                          </a>
                        ) : null}
                      </div>
                      {partner.description ? (
                        <p className="mt-2 line-clamp-2 max-w-2xl text-sm text-neutral-600">
                          {partner.description}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-none"
                      onClick={() => togglePartnerExpanded(partner.id)}
                    >
                      <ChevronDown
                        className={cn(
                          'mr-1.5 h-4 w-4 transition-transform',
                          expanded && 'rotate-180'
                        )}
                      />
                      {expanded
                        ? tl('Masquer projets', 'Hide projects', 'Nascondi progetti')
                        : tl('Voir projets', 'View projects', 'Vedi progetti')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className="rounded-none"
                      onClick={() => handleAddProjectClick(partner.id)}
                    >
                      <Plus size={14} className="mr-1.5" />
                      {t('partners.addProject')}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="rounded-none"
                      onClick={() => handleEditClick(partner)}
                      aria-label={t('partners.editPartner')}
                    >
                      <Edit size={16} />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="rounded-none text-red-600 hover:bg-red-50 hover:text-red-700"
                          aria-label={t('partners.delete')}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('partners.deleteConfirm')}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('partners.deleteWarning', { name: partner.name })}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t('partners.cancel')}</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-red-500 hover:bg-red-600"
                            onClick={() => deletePartnerMutation.mutate(partner.id)}
                          >
                            {t('partners.delete')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>

                {expanded && (
                  <CardContent className="bg-neutral-50/60 p-4 sm:p-5">
                    {projects.length === 0 ? (
                      <div className="flex flex-col items-center border border-dashed border-neutral-300 bg-white px-4 py-12 text-center">
                        <FolderOpen className="mb-3 h-8 w-8 text-neutral-300" />
                        <p className="text-sm text-neutral-500">
                          {tl(
                            'Aucun projet pour ce partenaire.',
                            'No projects for this partner yet.',
                            'Nessun progetto per questo partner.'
                          )}
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-4 rounded-none"
                          onClick={() => handleAddProjectClick(partner.id)}
                        >
                          <Plus size={14} className="mr-1.5" />
                          {t('partners.addProject')}
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                        {projects.map((project) => {
                          const cover = project.images?.[0];
                          return (
                            <article
                              key={project.id}
                              className="group flex flex-col overflow-hidden border border-neutral-200 bg-white"
                            >
                              <button
                                type="button"
                                className="relative aspect-[4/3] w-full overflow-hidden bg-neutral-100 text-left"
                                onClick={() => handleEditProjectClick(project)}
                              >
                                {cover ? (
                                  <img
                                    src={cover}
                                    alt={project.title}
                                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                                  />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center text-neutral-400">
                                    <FolderOpen className="h-8 w-8" />
                                  </div>
                                )}
                                <div className="absolute left-0 top-0 flex flex-col items-start gap-1">
                                  {project.is_projects_hero && (
                                    <span className="bg-black px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
                                      {tl('Hero projets', 'Projects hero', 'Hero progetti')}
                                    </span>
                                  )}
                                  {project.showcase_position && (
                                    <span className="inline-flex items-center gap-1 bg-neutral-800 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
                                      <Star className="h-3 w-3" />
                                      {tl(
                                        `Accueil · slot ${project.showcase_position}`,
                                        `Home · slot ${project.showcase_position}`,
                                        `Home · slot ${project.showcase_position}`
                                      )}
                                    </span>
                                  )}
                                  {project.project_category &&
                                    project.project_category !== 'All' && (
                                      <span className="bg-neutral-800 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
                                        {project.project_category}
                                      </span>
                                    )}
                                </div>
                                <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all group-hover:bg-black/35 group-hover:opacity-100">
                                  <span className="bg-white px-3 py-2 text-xs font-semibold uppercase tracking-wider text-black">
                                    {tl('Modifier', 'Edit', 'Modifica')}
                                  </span>
                                </div>
                              </button>

                              <div className="flex flex-1 flex-col gap-3 p-3">
                                <div className="min-w-0 flex-1">
                                  <h3 className="truncate text-sm font-semibold uppercase tracking-[0.08em]">
                                    {project.title}
                                  </h3>
                                  {project.location ? (
                                    <p className="mt-1 flex items-center gap-1 text-xs text-neutral-500">
                                      <MapPin className="h-3 w-3" />
                                      {project.location}
                                    </p>
                                  ) : null}
                                  {project.description ? (
                                    <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-neutral-600">
                                      {project.description}
                                    </p>
                                  ) : null}
                                  {project.images?.length ? (
                                    <p className="mt-2 text-[11px] text-neutral-400">
                                      {project.images.length}{' '}
                                      {tl(
                                        project.images.length === 1 ? 'image' : 'images',
                                        project.images.length === 1 ? 'image' : 'images',
                                        project.images.length === 1 ? 'immagine' : 'immagini'
                                      )}
                                    </p>
                                  ) : null}
                                </div>

                                <div className="flex items-center gap-2 border-t border-neutral-100 pt-3">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="flex-1 rounded-none"
                                    onClick={() => handleEditProjectClick(project)}
                                  >
                                    <Edit size={14} className="mr-1.5" />
                                    {tl('Modifier', 'Edit', 'Modifica')}
                                  </Button>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="rounded-none text-red-600 hover:bg-red-50 hover:text-red-700"
                                      >
                                        <Trash2 size={16} />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>
                                          {t('partners.deleteConfirm')}
                                        </AlertDialogTitle>
                                        <AlertDialogDescription>
                                          {t('partners.deleteProjectWarning', {
                                            title: project.title,
                                          })}
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>{t('partners.cancel')}</AlertDialogCancel>
                                        <AlertDialogAction
                                          className="bg-red-500 hover:bg-red-600"
                                          onClick={() =>
                                            deleteProjectMutation.mutate(project.id)
                                          }
                                        >
                                          {t('partners.delete')}
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              </div>
                            </article>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
      
      {/* Add Partner Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px] text-black">
          <DialogHeader>
            <DialogTitle>{t('partners.addPartner')}</DialogTitle>
            <DialogDescription>
              {t('partners.addPartnerDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <form className="text-black" onSubmit={(e) => {
            e.preventDefault();
            handleAddPartner();
          }}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  {t('partners.name')}
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="col-span-3"
                  required
                />
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="logo" className="text-right">
                  {t('partners.logo')}
                </Label>
                <div className="col-span-3 space-y-2">
                  <Input
                    id="logo"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="col-span-3"
                    required
                  />
                  {logoPreview && (
                    <div className="w-24 h-24 rounded-md bg-gray-50 p-2 flex items-center justify-center">
                      <img src={logoPreview} alt="Logo preview" className="max-h-full max-w-full object-contain" />
                    </div>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="location" className="text-right">
                  {t('partners.location') || 'Location'}
                </Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="col-span-3"
                  placeholder="e.g., Tunis, Sousse"
                />
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="website" className="text-right">
                  {t('partners.website')}
                </Label>
                <Input
                  id="website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  className="col-span-3"
                  placeholder="https://example.com"
                />
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="description" className="text-right">
                  {t('partners.description')}
                </Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="col-span-3"
                  rows={3}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => {
                  resetForm();
                  setIsAddDialogOpen(false);
                }}
              >
                {t('partners.cancel')}
              </Button>
              <Button 
                type="submit"
                disabled={!formData.name || (!logoFile && !formData.logo) || isUploading}
              >
                {isUploading ? t('partners.uploading') : (
                  <>
                    <Save size={16} className="mr-2" /> {t('partners.save')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Edit Partner Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px] text-black">
          <DialogHeader>
            <DialogTitle>{t('partners.editPartner')}</DialogTitle>
            <DialogDescription>
              {t('partners.editPartnerDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <form className="text-black" onSubmit={(e) => {
            e.preventDefault();
            handleUpdatePartner();
          }}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-name" className="text-right">
                  {t('partners.name')}
                </Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="col-span-3"
                  required
                />
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-logo" className="text-right">
                  {t('partners.logo')}
                </Label>
                <div className="col-span-3 space-y-2">
                  <Input
                    id="edit-logo"
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="col-span-3"
                  />
                  {logoPreview && (
                    <div className="w-24 h-24 rounded-md bg-gray-50 p-2 flex items-center justify-center">
                      <img src={logoPreview} alt="Logo preview" className="max-h-full max-w-full object-contain" />
                    </div>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-location" className="text-right">
                  {t('partners.location') || 'Location'}
                </Label>
                <Input
                  id="edit-location"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="col-span-3"
                  placeholder="e.g., Tunis, Sousse"
                />
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-website" className="text-right">
                  {t('partners.website')}
                </Label>
                <Input
                  id="edit-website"
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({...formData, website: e.target.value})}
                  className="col-span-3"
                  placeholder="https://example.com"
                />
              </div>
              
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="edit-description" className="text-right">
                  {t('partners.description')}
                </Label>
                <Textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="col-span-3"
                  rows={3}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                type="button"
                variant="outline" 
                onClick={() => {
                  resetForm();
                  setIsEditDialogOpen(false);
                }}
              >
                {t('partners.cancel')}
              </Button>
              <Button 
                type="submit"
                disabled={!formData.name || isUploading}
              >
                {isUploading ? t('partners.uploading') : (
                  <>
                    <Save size={16} className="mr-2" /> {t('partners.update')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Add Project Dialog */}
      <Dialog 
        open={isProjectDialogOpen} 
        onOpenChange={(open) => {
          if (!open) {
            setSelectedPartnerId(null);
            setSelectedProject(null);
            resetProjectForm();
          }
          setIsProjectDialogOpen(open);
        }}
      >
        <DialogContent className="h-[95vh] max-h-[95vh] w-full max-w-3xl overflow-y-auto rounded-none text-black sm:max-w-3xl">
          <DialogHeader className="border-b border-neutral-200 pb-4">
            <DialogTitle className="text-xl font-semibold tracking-tight">
              {selectedProject
                ? `${t('partners.editProject')} — ${selectedProject.title}`
                : `${t('partners.addProject')} — ${partners.find((p) => p.id === selectedPartnerId)?.name || ''}`}
            </DialogTitle>
            <DialogDescription className="text-sm text-neutral-500">
              {selectedProject
                ? t('partners.editProjectDescription')
                : t('partners.addProjectDescription')}
            </DialogDescription>
            {selectedProject?.images?.[0] && (
              <div className="mt-3 aspect-[21/9] w-full overflow-hidden bg-neutral-100">
                <img
                  src={selectedProject.images[0]}
                  alt={selectedProject.title}
                  className="h-full w-full object-cover"
                />
              </div>
            )}
          </DialogHeader>
          
          <form className="text-black" onSubmit={(e) => {
            e.preventDefault();
            selectedProject ? handleUpdateProject() : handleAddProject();
          }}>
            <div className="grid gap-6 py-4">
              <div className="space-y-2">
                <Label htmlFor="project-title" className="text-sm font-medium">
                  {t('partners.projectTitle')}
                </Label>
                <Input
                  id="project-title"
                  value={projectFormData.title}
                  onChange={(e) => setProjectFormData({...projectFormData, title: e.target.value})}
                  className="w-full"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="project-description" className="text-sm font-medium">
                  {t('partners.projectDescription')}
                </Label>
                <Textarea
                  id="project-description"
                  value={projectFormData.description}
                  onChange={(e) => setProjectFormData({...projectFormData, description: e.target.value})}
                  className="w-full"
                  rows={4}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="project-location" className="text-sm font-medium">
                  {t('partners.projectLocation') || 'Location'}
                </Label>
                <Input
                  id="project-location"
                  value={projectFormData.location || ''}
                  onChange={(e) => setProjectFormData({...projectFormData, location: e.target.value})}
                  className="w-full"
                  placeholder="e.g., Tunis, Sousse"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="project-category" className="text-sm font-medium">
                  {t('partners.projectCategory')}
                </Label>
                <Select
                  value={projectFormData.project_category}
                  onValueChange={(value) => setProjectFormData({...projectFormData, project_category: value as Project['project_category']})}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('partners.selectCategory')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">{t('partners.categories.all')}</SelectItem>
                    <SelectItem value="Hotels">{t('partners.categories.hotels')}</SelectItem>
                    <SelectItem value="Villas">{t('partners.categories.villas')}</SelectItem>
                    <SelectItem value="Restaurants">{t('partners.categories.restaurants')}</SelectItem>
                    <SelectItem value="Coffee shops">{t('partners.categories.coffeeShops')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-row items-start space-x-3 border border-neutral-200 bg-neutral-50 p-4">
                <Checkbox
                  id="project-hero"
                  checked={!!projectFormData.is_projects_hero}
                  onCheckedChange={(checked) =>
                    setProjectFormData({
                      ...projectFormData,
                      is_projects_hero: checked === true,
                    })
                  }
                />
                <div className="space-y-1 leading-none">
                  <Label htmlFor="project-hero" className="cursor-pointer text-sm font-medium">
                    {tl(
                      'Projet hero (page Projets)',
                      'Hero project (Projects page)',
                      'Progetto hero (pagina Progetti)'
                    )}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {tl(
                      'Image principale en haut de /projects. Un seul projet à la fois.',
                      'Main banner at the top of /projects. Only one project at a time.',
                      'Banner principale in cima a /projects. Un solo progetto alla volta.'
                    )}
                  </p>
                </div>
              </div>

              <div className="space-y-3 border border-neutral-200 bg-neutral-50 p-4">
                <div>
                  <Label className="text-sm font-medium">
                    {tl(
                      'Emplacement page d’accueil',
                      'Homepage placement',
                      'Posizione in homepage'
                    )}
                  </Label>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {tl(
                      'Choisissez la case dans la grille 2 + 1 sous Collection en tendance. Enregistrez pour appliquer.',
                      'Choose the slot in the 2 + 1 grid under Trending Collection. Save to apply.',
                      'Scegli lo slot nella griglia 2 + 1. Salva per applicare.'
                    )}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {(
                    [
                      {
                        value: '1' as const,
                        label: tl('1 — Haut gauche', '1 — Top left', '1 — Alto sinistra'),
                        className: 'col-span-1',
                      },
                      {
                        value: '2' as const,
                        label: tl('2 — Haut droite', '2 — Top right', '2 — Alto destra'),
                        className: 'col-span-1',
                      },
                      {
                        value: '3' as const,
                        label: tl('3 — Bas pleine largeur', '3 — Bottom full width', '3 — Basso intero'),
                        className: 'col-span-2',
                      },
                    ] as const
                  ).map((slot) => {
                    const selected =
                      String(projectFormData.showcase_position ?? '') === slot.value;
                    return (
                      <button
                        key={slot.value}
                        type="button"
                        onClick={() => handleShowcasePositionChange(slot.value)}
                        className={cn(
                          'border px-3 py-4 text-left text-sm transition-colors',
                          slot.className,
                          selected
                            ? 'border-black bg-black text-white'
                            : 'border-neutral-300 bg-white text-black hover:border-black'
                        )}
                      >
                        {slot.label}
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => handleShowcasePositionChange('none')}
                  className={cn(
                    'w-full border px-3 py-2 text-left text-xs uppercase tracking-wider transition-colors',
                    !projectFormData.showcase_position
                      ? 'border-black bg-neutral-100 text-black'
                      : 'border-neutral-300 bg-white text-neutral-600 hover:border-black'
                  )}
                >
                  {tl('Pas sur l’accueil', 'Not on homepage', 'Non in homepage')}
                </button>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="project-images" className="text-sm font-medium">
                  {t('partners.projectImages')}
                </Label>
                <div className="space-y-4">
                  <Input
                    id="project-images"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleProjectImagesChange}
                    disabled={isConvertingImages || isUploading}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('partners.avifHint') ||
                      'Les images sont converties en AVIF (plus légères) avant l’upload.'}
                  </p>
                  {(isConvertingImages || conversionProgress) && (
                    <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="truncate">
                          {conversionProgress?.message ||
                            (t('partners.convertingImages') || 'Conversion AVIF…')}
                        </span>
                        <span className="shrink-0 font-medium tabular-nums">
                          {conversionProgress?.overall ?? 0}%
                        </span>
                      </div>
                      <Progress value={conversionProgress?.overall ?? 0} className="h-2" />
                      {conversionProgress && conversionProgress.total > 1 && (
                        <p className="text-xs text-muted-foreground">
                          {conversionProgress.currentIndex}/{conversionProgress.total}
                          {conversionProgress.fileName ? ` — ${conversionProgress.fileName}` : ''}
                        </p>
                      )}
                    </div>
                  )}
                  {/* Show existing images when editing */}
                  {projectFormData.images && projectFormData.images.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {projectFormData.images.map((image, index) => (
                        <div key={`${image}-${index}`} className="relative group">
                          <img
                            src={image}
                            alt={`Project image ${index + 1}`}
                            className="w-full h-24 object-cover rounded-md"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setProjectFormData({
                                ...projectFormData,
                                images: projectFormData.images.filter((_, i) => i !== index)
                              });
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Show new images being uploaded */}
                  {projectImages.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {projectImages.map((file, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`New image ${index + 1}`}
                            className="w-full h-24 object-cover rounded-md"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setProjectImages(projectImages.filter((_, i) => i !== index));
                            }}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="project-products-search" className="text-sm font-medium">
                  {t('partners.projectProducts')}
                </Label>
                <div className="w-full space-y-3">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="project-products-search"
                      value={productSearchQuery}
                      onChange={(e) => setProductSearchQuery(e.target.value)}
                      placeholder={t('partners.searchProducts') || 'Rechercher un produit…'}
                      className="w-full pl-10 pr-10"
                    />
                    {productSearchQuery && (
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        onClick={() => setProductSearchQuery('')}
                        aria-label="Clear search"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="max-h-56 overflow-y-auto rounded-md border border-border">
                    {filteredProducts.length === 0 ? (
                      <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                        {allProducts.length === 0
                          ? (t('partners.noProductsAvailable') || 'Aucun produit disponible')
                          : (t('partners.noProductsFound') || 'Aucun produit trouvé')}
                      </p>
                    ) : (
                      <ul className="divide-y divide-border">
                        {filteredProducts.map((product) => {
                          const selected = projectFormData.products?.includes(product.name);
                          return (
                            <li key={product.id}>
                              <button
                                type="button"
                                onClick={() => toggleProjectProduct(product.name)}
                                className={`flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted/60 ${
                                  selected ? 'bg-primary/10' : ''
                                }`}
                              >
                                <span
                                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                    selected
                                      ? 'border-primary bg-primary text-primary-foreground'
                                      : 'border-muted-foreground/40'
                                  }`}
                                  aria-hidden
                                >
                                  {selected ? '✓' : ''}
                                </span>
                                {(product.image_nobg || product.images?.[0]) && (
                                  <img
                                    src={product.image_nobg || product.images[0]}
                                    alt=""
                                    className="h-9 w-9 rounded object-cover bg-muted"
                                  />
                                )}
                                <span className="min-w-0 flex-1 truncate font-medium">{product.name}</span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>

                  {projectFormData.products && projectFormData.products.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {projectFormData.products.map((product, index) => (
                        <div
                          key={`${product}-${index}`}
                          className="flex items-center gap-1 bg-primary/10 px-3 py-1.5 rounded-full text-sm"
                        >
                          <span className="truncate max-w-[160px]">{product}</span>
                          <button
                            type="button"
                            onClick={() => toggleProjectProduct(product)}
                            className="text-primary hover:text-primary/80 ml-1"
                            aria-label={`Remove ${product}`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <DialogFooter className="flex flex-col sm:flex-row gap-3 pt-6">
              <Button 
                type="button"
                variant="outline" 
                onClick={() => {
                  resetProjectForm();
                  setSelectedProject(null);
                  setIsProjectDialogOpen(false);
                }}
                className="w-full sm:w-auto"
              >
                {t('partners.cancel')}
              </Button>
              <Button 
                type="submit"
                disabled={
                  !projectFormData.title ||
                  !projectFormData.description ||
                  isUploading ||
                  isConvertingImages
                }
                className="w-full sm:w-auto"
              >
                {isUploading ? t('partners.uploading') : isConvertingImages ? (
                  t('partners.convertingImages') || 'Conversion…'
                ) : (
                  <>
                    <Save size={16} className="mr-2" /> {t('partners.save')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnersManagement;
